import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Send, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import api from '../../lib/api';
import { formatDate } from './PostCardKit';

function CommentRow({ c, onReply, compact }) {
    return (
        <div className={`flex gap-2 ${compact ? "" : "bg-slate-50 rounded-xl p-3"}`}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-orange-400 flex-shrink-0
                            flex items-center justify-center text-white text-xs font-medium">
                {(c.author_user_id || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0 text-sm">
                <div className="text-slate-700 whitespace-pre-wrap">{c.body}</div>
                <div className="text-xs text-slate-400 mt-1 flex gap-3">
                    <span>{formatDate(c.created_at)}</span>
                    {c.edited_at && <span className="italic">modifié</span>}
                    {!compact && onReply && (
                        <button onClick={onReply} data-testid="comment-reply-btn"
                                className="hover:underline text-[#681793]">Répondre</button>
                    )}
                </div>
            </div>
        </div>
    );
}

export function CommentsPanel({ post, onClose, onPosted }) {
    const [comments, setComments] = useState([]);
    const [text, setText] = useState("");
    const [replyTo, setReplyTo] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/posts/${post.content_id || post.id}/comments`);
            setComments(r.data.items || []);
        } catch { /* ignore */ }
    }, [post]);

    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try {
            await api.post(`/posts/${post.content_id || post.id}/comments`, {
                body: text.trim(), parent_comment_id: replyTo,
            });
            setText(""); setReplyTo(null);
            await load();
            onPosted && onPosted();
        } finally { setBusy(false); }
    };

    const topLevel = comments.filter(c => !c.parent_comment_id);
    const replies = (parentId) => comments.filter(c => c.parent_comment_id === parentId);

    return (
        <div data-testid="comments-panel"
             className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
             onClick={onClose}>
            <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[85vh] flex flex-col"
                 onClick={e => e.stopPropagation()}>
                <header className="p-4 border-b flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Commentaires</h3>
                    <button onClick={onClose} aria-label="Fermer" data-testid="comments-close-btn"
                            className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
                </header>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {topLevel.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-6">
                            Aucun commentaire pour le moment.
                        </p>
                    )}
                    {topLevel.map(c => (
                        <div key={c.id} data-testid={`comment-${c.id}`}>
                            <CommentRow c={c} onReply={() => setReplyTo(c.id)} />
                            {replies(c.id).map(r => (
                                <div key={r.id} className="ml-8 mt-2" data-testid={`comment-${r.id}`}>
                                    <CommentRow c={r} compact />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <footer className="p-3 border-t">
                    {replyTo && (
                        <div className="text-xs text-slate-500 mb-2 flex items-center justify-between">
                            <span>Réponse à un commentaire</span>
                            <button onClick={() => setReplyTo(null)} className="underline">annuler</button>
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Textarea data-testid="comment-input" rows={2} value={text}
                                  onChange={e => setText(e.target.value)}
                                  placeholder="Écrire un commentaire respectueux et utile…"
                                  className="flex-1 text-sm" />
                        <Button disabled={!text.trim() || busy} onClick={submit}
                                data-testid="comment-submit-btn"
                                className="bg-[#681793] hover:bg-[#5a1380] self-end">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                    </div>
                </footer>
            </div>
        </div>
    );
}
